// Milestones API client for unified platform
import { mockMilestones, mockHolidays } from './mockData.js';

const API_BASE = import.meta.env.VITE_API_BASE || 'https://u5aw5omfkd.execute-api.us-east-2.amazonaws.com/dev';
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true';

export async function saveUnitsSchedule({ project_id, building_id, unit_number, unit }) {
  if (USE_MOCK) {
    console.log('Mock save unit schedule:', { project_id, building_id, unit_number, unit });
    return { success: true };
  }
  
  const response = await fetch(`${API_BASE}/milestones/units`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      project_id,
      building_id,
      unit_number,
      milestones: unit
    })
  });
  
  if (!response.ok) {
    throw new Error(`Failed to save unit schedule: ${response.statusText}`);
  }
  
  return response.json();
}

export async function saveBuildingSchedule({ project_id, building_id, building }) {
  if (USE_MOCK) {
    console.log('Mock save building schedule:', { project_id, building_id, building });
    return { success: true };
  }
  
  const response = await fetch(`${API_BASE}/milestones/buildings`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      project_id,
      building_id,
      milestones: building
    })
  });
  
  if (!response.ok) {
    throw new Error(`Failed to save building schedule: ${response.statusText}`);
  }
  
  return response.json();
}

export async function readBuildingSchedules({ project_id, building_id }) {
  if (USE_MOCK) {
    return { items: [] };
  }
  
  // Use timeline endpoint since milestones/buildings doesn't exist
  const timeline = await fetchMilestonesTimeline({ project_id });
  
  // Filter units for the specific building and format as items
  const buildingUnits = (timeline.units || []).filter(unit => 
    unit.building_id === building_id
  );
  
  return { items: buildingUnits };
}

export async function fetchMilestonesTimeline(params = {}) {
  try {
    const url = new URL(`${API_BASE}/timeline`);
    if (params.project_id) url.searchParams.set('project_id', params.project_id);
    if (params.start) url.searchParams.set('start', params.start);
    if (params.end) url.searchParams.set('end', params.end);
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Timeline API error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.warn('Timeline API failed, using mock data:', error.message);
    return createMockTimelineData();
  }
}

function createMockTimelineData() {
  return {
    units: [
      {
        project_id: "Aria",
        building_id: "1",
        unit_number: "101",
        milestones: {
          unit_frame_inspection: "2024-01-15",
          drywall_nail_inspection: "2024-02-01",
          projected_coe: "2024-06-15"
        },
        sales_status: {
          status_key: "inventory",
          status_label: "Available",
          status_color: "#31a354",
          status_date: "2024-01-01",
          projected_coe: "2024-06-15"
        }
      },
      {
        project_id: "Aria", 
        building_id: "1",
        unit_number: "102",
        milestones: {
          unit_frame_inspection: "2024-02-01",
          drywall_nail_inspection: "2024-02-15",
          drywall_texture: "2024-03-01",
          projected_coe: "2024-07-01"
        },
        sales_status: {
          status_key: "backlog",
          status_label: "Ratified",
          status_color: "#fdae6b",
          status_date: "2024-02-01",
          projected_coe: "2024-07-01"
        }
      },
      {
        project_id: "Aria",
        building_id: "1", 
        unit_number: "103",
        milestones: {
          unit_frame_inspection: "2024-01-01",
          drywall_nail_inspection: "2024-01-15",
          drywall_texture: "2024-02-01",
          install_cabinets: "2024-02-15",
          appliance_delivery: "2024-03-01",
          buyer_orientation: "2024-03-15"
        },
        sales_status: {
          status_key: "closed",
          status_label: "Closed",
          status_color: "#e34a33",
          status_date: "2024-03-20"
        }
      },
      {
        project_id: "Fusion",
        building_id: "2",
        unit_number: "201",
        milestones: {
          unit_frame_inspection: "2024-03-01",
          drywall_nail_inspection: "2024-03-15",
          drywall_texture: "2024-04-01",
          projected_coe: "2024-08-15"
        },
        sales_status: {
          status_key: "offer",
          status_label: "Under Contract",
          status_color: "#fd8d3c",
          status_date: "2024-03-10",
          projected_coe: "2024-08-15"
        }
      }
    ],
    summary: {
      buildings: 2,
      units: 4,
      missing_building_anchor: 0,
      missing_unit_anchor: 0,
      units_with_sales_data: 4,
      units_missing_sales_data: 0
    },
    sales_sync: {
      matched_units: 4,
      latest_updated_at: new Date().toISOString(),
      warnings: []
    }
  };
}
